import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PaperPlaneTilt, CheckCircle, UserCheck, Sparkle, ArrowClockwise } from "@phosphor-icons/react";
import client from "../lib/api";
import { Button } from "./ui/button";

const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return "—"; } };

export default function LeadsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState("");

  const load = () => {
    setLoading(true);
    client.get("/lpg/demo/leads").then((r) => setData(r.data)).catch(() => toast.error("Couldn't load leads")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const invite = async (lead) => {
    setSending(lead.email);
    try {
      await client.post("/lpg/demo/leads/invite", { email: lead.email, address: lead.address });
      toast.success(`Invite sent to ${lead.email}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send invite"); }
    setSending("");
  };

  const leads = data?.leads || [];

  return (
    <div data-testid="leads-panel">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#B08D57] font-semibold">Funnel</p>
          <h1 className="font-display text-3xl text-[#4A0000] mt-1">Free-Demo Leads</h1>
          <p className="text-sm text-[#5C5A58] mt-1">Everyone who generated a free presentation on the public demo page.</p>
        </div>
        <button onClick={load} data-testid="leads-refresh" className="flex items-center gap-1.5 text-sm text-[#8A6A2F] hover:text-[#4A0000] transition-colors">
          <ArrowClockwise size={15} /> Refresh
        </button>
      </div>

      {data && (
        <div className="grid grid-cols-3 gap-3 mt-6 max-w-xl">
          {[["Total leads", data.total], ["Converted", data.converted], ["Invited", data.invited]].map(([l, v]) => (
            <div key={l} className="rounded-xl border border-[#E9DDC6] bg-white p-4">
              <p className="font-display text-2xl text-[#4A0000]">{v}</p>
              <p className="text-xs text-[#8A8782] mt-0.5">{l}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 rounded-xl border border-[#E9DDC6] bg-white overflow-hidden">
        {loading ? (
          <p className="p-8 text-center text-sm text-[#8A8782]">Loading leads…</p>
        ) : leads.length === 0 ? (
          <p className="p-8 text-center text-sm text-[#8A8782]" data-testid="leads-empty">No demo leads yet. They'll appear here as people try the free generator.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-[#8A8782] border-b border-[#F0ECE2]">
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Property</th>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.email} className="border-b border-[#F6F3EC] last:border-0 hover:bg-[#FBF9F4]" data-testid={`lead-row-${l.email}`}>
                    <td className="px-4 py-3 font-medium text-[#1A1715] break-all">{l.email}</td>
                    <td className="px-4 py-3 text-[#5C5A58] max-w-[220px] truncate" title={l.address}>{l.address || "—"}</td>
                    <td className="px-4 py-3 text-[#8A8782] whitespace-nowrap">{fmt(l.created_at)}</td>
                    <td className="px-4 py-3">
                      {l.has_account ? (
                        <span className="inline-flex items-center gap-1 text-[12px] font-medium text-[#2E7D5B]"><UserCheck size={14} weight="fill" />Signed up</span>
                      ) : l.invite_sent ? (
                        <span className="inline-flex items-center gap-1 text-[12px] font-medium text-[#B08D57]"><CheckCircle size={14} weight="fill" />Invited</span>
                      ) : (
                        <span className="text-[12px] text-[#8A8782]">New lead</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {l.has_account ? (
                        <span className="text-[12px] text-[#A8A49E]">—</span>
                      ) : (
                        <Button data-testid={`lead-invite-${l.email}`} onClick={() => invite(l)} disabled={sending === l.email}
                          size="sm" className={`h-8 ${l.invite_sent ? "bg-white border border-[#E9DDC6] text-[#4A0000] hover:bg-[#F6F3EC]" : "bg-[#4A0000] hover:bg-[#5C1A1A] text-white"}`}>
                          {l.invite_sent ? <><PaperPlaneTilt size={13} className="mr-1" />Resend</> : <><Sparkle size={13} weight="fill" className="mr-1" />Invite to trial</>}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
